"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Validation = void 0;
/**
 * The `Validation` has the responsibility to
 * perform validations and manage validation results.
 */
class Validation {
    constructor(issue) {
        this.issues = [];
        if (issue) {
            this.add(issue);
        }
    }
    /**
     * Creates an empty Validation
     *
     * @returns The created Validation.
     */
    static empty() {
        return new Validation();
    }
    /**
     * Creates a Validation depended on the result
     *
     * @param expression The Validation result
     * @param issue The Validation result arguments
     * @returns The created Validation.
     */
    static create(expression, issue) {
        return expression ? Validation.success() : Validation.fail(issue);
    }
    /**
     * Creates a Validation success
     *
     * @returns The created Validation.
     */
    static success() {
        return new Validation();
    }
    /**
     * Creates a Validation fail
     *
     * @param issue The Validation result arguments
     * @returns The created Validation.
     */
    static fail(issue) {
        return new Validation(issue);
    }
    /**
     * Creates a Validation fail and immediately throws it if there are any issues
     *
     * @param issue The Validation result arguments
     * @returns The created Validation.
     */
    static throws(issue) {
        return Validation.fail(issue).throws();
    }
    /**
     * Add issues to the result object
     *
     * @param issue Either an issue or an array of issues
     */
    add(issue) {
        if (!issue)
            throw new Error('Validation.add: issue is not defined');
        if (issue instanceof Validation) {
            this.mergeValidation(issue);
            return this;
        }
        if (Array.isArray(issue)) {
            for (const argItem of issue) {
                if (argItem instanceof Validation) {
                    this.mergeValidation(argItem);
                }
                else {
                    this.addIssue(argItem);
                }
            }
            return this;
        }
        this.addIssue(issue);
        return this;
    }
    /**
     * Raises an `Error` if there are any issues.
     * Returns the Validation instance if there are none.
     */
    throws() {
        if (this.hasIssue()) {
            throw this.toError();
        }
        return this;
    }
    mergeValidation(validation) {
        this.issues = this.issues.concat(validation.getIssues());
    }
    addIssue(issue) {
        this.issues.push(issue);
    }
    getIssues() {
        return this.issues;
    }
    getIssueKeys() {
        return this.issues.map((i) => {
            if (typeof i === 'string') {
                return i;
            }
            return i.messageKey;
        });
    }
    /**
     * Retrieve all issues except the ones given.
     *
     * @param ignore The validation keys to ignore
     */
    getIssuesExcept(...ignore) {
        if (!this.issues)
            return [];
        if (!ignore)
            return this.getIssues();
        return this.issues.filter((issue) => ignore.indexOf(issue) < 0);
    }
    /**
     * Returns if this `Validation` instance contains a given issue.
     * If no issue is given, returns if there are any issues
     *
     * @param [issue] The optional issue
     */
    hasIssue(issue) {
        if (issue != null) {
            return this.issues.some((x) => x === issue);
        }
        return Boolean(this.issues.length);
    }
    /**
     * Returns if this `Validation` instance has any issues.
     */
    hasIssues() {
        return this.hasIssue();
    }
    /**
     * Creates an error object
     *
     * @returns The created `Error`
     */
    toError() {
        return new Error(this.toString());
    }
    toString() {
        return JSON.stringify(this.issues);
    }
}
exports.Validation = Validation;
//# sourceMappingURL=Validation.js.map